'use client';

import { Button } from '@common/components';
import { SvgFlash1Fill } from '@common/icon';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { POLICY_APPLICATION_TYPE } from '@/api';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { TermsAgreementCheckList } from '@/components/organism/TermsAgreementCheckList';
import { ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { jejuYouthDreamQueryOptions } from '@/query-factory/jeju-youthdream';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';

interface QuickApplyButtonProps {
  policyApplyId: number;
  policyApplicationType: string;
}

export const QuickApplyButton = ({
  policyApplyId,
  policyApplicationType,
}: QuickApplyButtonProps) => {
  const memberOAuth = useAuthStore((state) => state.myInfo?.memberOAuth);

  const isKbWebview = useDeviceInfoStore(
    (state) => state.webviewType === WEBVIEW_TYPE.KB_PAY,
  );

  const jejuApplyList = useInfiniteQuery(
    jejuYouthDreamQueryOptions.applyList(memberOAuth, policyApplyId),
  );

  const applyType = (() => {
    if (isKbWebview) return null;

    switch (policyApplicationType) {
      case POLICY_APPLICATION_TYPE.JEJU_CONSULTATION: {
        const jejuApplyListCount = jejuApplyList.data?.totalCount;
        if (jejuApplyListCount === undefined) return null;

        return jejuApplyListCount
          ? '제주청년이어드림내역'
          : '제주청년이어드림신청';
      }

      case POLICY_APPLICATION_TYPE.SCHOLARSHIP_FOUNDATION:
        return '학자금채무자신고';
      default:
        return null;
    }
  })();

  const router = useCustomRouter();

  const [isOpened, setIsOpened] = useState(false);

  return applyType ? (
    <>
      <Button
        className="rounded-smal mb-12 w-full"
        dimensions="H48"
        onClick={(e) => {
          e.preventDefault();

          switch (applyType) {
            case '학자금채무자신고':
              return router.push(ROUTES.POLICY_CONSENT.pathname);
            case '제주청년이어드림신청':
              return setIsOpened(true);
            case '제주청년이어드림내역':
              return router.push(ROUTES.MY_APPLY.pathname);
          }
        }}
      >
        <SvgFlash1Fill className="size-18" />
        {applyType === '제주청년이어드림내역' ? '신청내역' : '바로신청'}
      </Button>
      {applyType === '제주청년이어드림신청' ? (
        <ResponsiveDialog
          opened={isOpened}
          onClose={() => setIsOpened(false)}
          drawerType="bottom"
        >
          <TermsAgreementCheckList />
        </ResponsiveDialog>
      ) : null}
    </>
  ) : null;
};
